import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import React from 'react';
import { fmtCurrency, fmtPct } from './contactPortfolioFormatUtils';
import type { OverviewMetrics, PortfolioSummary } from './contactPortfolioTypes';

export default function ContactPortfolioHeroCard({
  summary,
  overviewMetrics,
  loading,
}: {
  summary: PortfolioSummary | null;
  overviewMetrics: OverviewMetrics | null;
  loading: boolean;
}) {
  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 3,
        background: 'linear-gradient(135deg, #1a3a2a 0%, #2e5c42 100%)',
        color: '#fff',
        p: 3,
        mb: 3,
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: '#d8eadf', letterSpacing: 1.5, display: 'block', mb: 0.5 }}
      >
        Portfolio
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2, mb: 1.5 }}>
        {fmtCurrency(summary?.committedInvestment)}
      </Typography>

      {loading ? (
        <Box sx={{ height: 64, display: 'flex', alignItems: 'center' }}>
          <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.7)' }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            {[
              { label: 'IRR', value: fmtPct(overviewMetrics?.projectedIrrPercent) },
              { label: 'ROE', value: fmtPct(overviewMetrics?.projectedRoePercent) },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  flexGrow: 1,
                  flexBasis: '28%',
                  minWidth: 96,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 2,
                  py: 1.25,
                  px: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#d8eadf',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {label}
                </Typography>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                  {value}
                </Typography>
              </Box>
            ))}
            <Box sx={{ flexGrow: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {[
              { label: 'Deployed Capital', value: fmtCurrency(summary?.deployedCapital) },
              { label: 'Forecast Return', value: fmtCurrency(summary?.totalForecastReturn) },
              {
                label: 'Active Funds',
                value: summary?.activeFunds != null ? String(summary.activeFunds) : '—',
              },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  flexGrow: 1,
                  flexBasis: '28%',
                  minWidth: 96,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  borderRadius: 2,
                  py: 1.25,
                  px: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#d8eadf',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {label}
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
}
